<template>
  <div>
    <el-dialog
      title="フォローチェック"
      :visible.sync="visible"
      custom-class="with-title"
      :show-close="!loading"
      :before-close="close"
    >
      <div v-loading="loading" :element-loading-text="loading_text">
        <div class="section-container">
          <Message type="info">
            公式TikTokからダウンロードしたデータを元に、フォローチェックします。
            <br />
            アップロードするデータのダウンロード方法は、ヘルプをご確認ください。
            <br />
            <a href="https://help-atelu.comnico.jp/19905779-6738-808e-8855-e2fe3959d939" target="_blank">
              ヘルプ: 応募者のフォロー状況をチェックする
            </a>
          </Message>
        </div>

        <div class="section-container">
          <Panel>
            <div class="section-item">
              <FormItemLabel label="データをアップロード" />
            </div>

            <div v-if="!temp_followers.length" class="section-item">
              user_data_tiktok.jsonをアップロードしてください。
            </div>

            <div v-if="is_type_error" class="section-item">
              <Message type="danger">
                ファイル形式が不適切なため、アップロードに失敗しました。
                <br />
                正しいファイルを選択してください。
              </Message>
            </div>

            <div v-if="is_read_error" class="section-item">
              <Message type="danger"> アップロードに失敗しました。時間を置いて再度お試しください。 </Message>
            </div>

            <div v-if="is_follow_check_error" class="section-item">
              <Message type="danger"> フォローチェックに失敗しました。時間を置いて再度お試しください。 </Message>
            </div>

            <div v-show="!temp_followers.length" class="section-item">
              <el-upload
                ref="upload"
                drag
                action=""
                :auto-upload="false"
                :on-change="readFile"
                :on-error="errorReadFile"
                :limit="1"
                :show-file-list="false"
              >
                <div class="upload-description">ここにファイルをドラッグ</div>
                <div class="upload-sub-description">または</div>
                <div class="upload-select-file">ファイルを選択</div>
              </el-upload>
            </div>

            <div v-if="temp_followers.length" class="section-item upload-file">
              <Icon name="attachment" />
              {{ this.temp_filename }}
              <div class="delete-button" @click="cancelFile()">
                <Icon name="close" size="small" />
              </div>
            </div>

            <div v-if="!temp_followers.length" class="section-item-tips">
              <Tips> キャンペーン実施アカウントのデータをアップロードしてください。 </Tips>
            </div>
            <div class="section-item-tips">
              <Tips> 取得できるのは、データダウンロード時点でのフォロー状況です。 </Tips>
            </div>
          </Panel>
        </div>

        <div class="section-footer">
          <el-button @click="close"> キャンセル </el-button>
          <el-button type="primary" @click="importFollowCheck" :disabled="disabled">
            <Icon name="check-circle" />
            フォローチェックを反映
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import Tips from '@/client/components/_atoms/Tips.vue'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import API from '@/client/core/api'
import { ApplicantState } from '@/client/store/useTikTokApplicantStore'
import { useTikTokApplicantStore } from '@/client/store/useTikTokApplicantStore'

export default defineComponent({
  components: {
    Icon,
    Message,
    Panel,
    Tips,
    FormItemLabel
  },
  data() {
    return {
      visible: false,
      is_type_error: false,
      is_read_error: false,
      is_follow_check_error: false,
      is_submit: false,
      is_progress: false,
      is_uploading: false,
      temp_filename: '',
      temp_followers: [] as string[]
    }
  },
  computed: {
    ...mapState(useTikTokApplicantStore, ['campaignPrize']),
    disabled() {
      return !this.temp_followers.length && !this.is_submit
    },
    loading() {
      return this.is_progress || this.is_uploading
    },
    loading_text() {
      if (this.is_progress) return 'ファイルを検証しています。しばらくお待ちください。'
      if (this.is_uploading) return 'フォローチェックを実行しています。しばらくお待ちください。'
      return ''
    }
  },
  methods: {
    ...mapActions(useTikTokApplicantStore, ['fetchApplicants']),
    /**
     * ダイアログを開く
     */
    open() {
      this.visible = true
    },

    /**
     * 有効なファイルかどうかを確認
     */
    async isValidFile(file: File) {
      this.is_read_error = false
      this.is_follow_check_error = false

      if (!file || !file.type || file.type !== 'application/json') {
        this.is_type_error = true
        this.is_progress = false

        return false
      }

      const file_data = await this.loadFile(file)

      if (!file_data) {
        this.is_type_error = true
        this.is_progress = false

        return false
      }

      const json = JSON.parse(file_data)

      if (!json) {
        this.is_type_error = true
        this.is_progress = false

        return false
      }

      return true
    },

    /**
     * ファイル読み込みエラー
     */
    errorReadFile() {
      this.is_type_error = false
      this.is_read_error = true
      this.is_follow_check_error = false
      this.is_progress = false
    },

    /**
     * ファイルの追加
     */
    async readFile(file: { raw: File; name: string }) {
      this.is_progress = true

      const is_valid = await this.isValidFile(file.raw)

      if (!is_valid) {
        ;(this.$refs.upload as { clearFiles: () => void }).clearFiles()
        return
      }

      const file_data = await this.loadFile(file.raw)

      const json = JSON.parse(file_data)

      this.temp_followers = json['Profile And Settings']?.['Follower']?.['FansList']?.map(v => v.UserName) || []

      if (this.temp_followers.length === 0) {
        this.is_type_error = true
        this.is_progress = false
        ;(this.$refs.upload as { clearFiles: () => void }).clearFiles()
        return
      }

      this.temp_filename = file.name

      this.is_type_error = false
      this.is_read_error = false
      this.is_follow_check_error = false
      this.is_progress = false
    },

    /**
     * ファイル読み込み
     */
    loadFile(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.onload = (e: ProgressEvent<FileReader>) => {
          resolve((e.target?.result as string) || '')
        }

        fileReader.onerror = (e: ProgressEvent<FileReader>) => {
          reject(e)
        }

        fileReader.readAsText(file)
      })
    },

    /**
     * ファイルアップロードの取り消し
     */
    cancelFile() {
      ;(this.$refs.upload as { clearFiles: () => void }).clearFiles()
      this.is_type_error = false
      this.is_read_error = false
      this.is_follow_check_error = false
      this.is_submit = false
      this.is_progress = false
      this.is_uploading = false
      this.temp_filename = ''
      this.temp_followers = []
    },

    /**
     * フォローチェックをインポートする
     */
    async importFollowCheck() {
      const vm = this as typeof this & {
        campaignPrize: ApplicantState['campaignPrize']
        fetchApplicants: () => void
      }

      if (this.is_submit) {
        return
      }

      this.is_submit = true
      this.is_uploading = true

      const result = await API.put(`/v1/tiktok_applicants/${vm.campaignPrize.id}/bulk_followers`, {
        followers: this.temp_followers
      })

      this.is_uploading = false

      setTimeout(() => {
        this.is_submit = false
      }, 2000)

      if (typeof result.data === 'undefined') {
        this.$notify({
          title: 'フォローチェックに失敗しました。',
          message: '',
          customClass: 'danger'
        })

        this.is_follow_check_error = true

        return
      }

      this.$notify({
        title: 'フォローチェックが完了しました。',
        message: '',
        customClass: 'success'
      })

      vm.fetchApplicants()
      this.close()
    },

    /**
     * ダイアログを閉じる
     */
    close() {
      ;(this.$refs.upload as { clearFiles: () => void }).clearFiles()
      this.is_type_error = false
      this.is_read_error = false
      this.is_follow_check_error = false
      this.is_submit = false
      this.is_progress = false
      this.is_uploading = false
      this.temp_filename = ''
      this.temp_followers = []
      this.visible = false
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.section-container {
  margin-bottom: $spacing-unit * 3;
}

.section-item {
  margin-bottom: $spacing-unit * 2;
}

.upload-description {
  font-weight: bold;
  color: $border-color;
}

.upload-sub-description {
  font-size: 11px;
  color: $border-color;
}

.upload-select-file {
  padding: 0 $spacing-unit;
  line-height: 30px;
  border: 1px solid $text-color;
  border-radius: 3px;
}

.upload-file {
  display: inline-block;
  padding: 0 $spacing-unit;
  line-height: $spacing-unit * 4;
  border: 1px solid $border-color;
  border-radius: 3px;
}

.delete-button {
  display: inline-block;
  margin-left: math.div($spacing-unit, 2);
  cursor: pointer;

  .mdi {
    line-height: 16px;
  }
}

.section-item-tips:not(:last-child) {
  margin-bottom: $spacing-unit;
}

.section-footer {
  text-align: right;
}

:deep(.el-upload) {
  display: block;
}

:deep(.el-upload-dragger) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  height: 116px;
}

:deep(.el-upload .el-button) {
  border-color: $text-color;
  box-shadow: none;
}
</style>
